import { copy } from "../copy";
import { formatMoney } from "../format";
import type { LandingWin } from "../feed";
import Icon from "../components/Icon";
import { compactCount, type Activity, type GlobalStats, type PublicReview } from "./data";

const t = copy.websites;
const section = "border-t border-ground-edge px-4 py-20 md:px-6 md:py-28";
const eyebrow = "text-[11px] uppercase tracking-[0.24em] text-chrome-dim";
const h2 = "mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl";

// Every figure here comes from GET /v1/stats/global. No stats, no section.
export function PlatformStats({ stats }: { stats: GlobalStats | null }) {
  if (!stats) return null;
  const items = [
    { value: stats.contests, label: t.stats.contests, note: t.stats.contestsNote, gold: true },
    { value: stats.submissions, label: t.stats.submissions, note: t.stats.submissionsNote, gold: false },
    { value: stats.reviews, label: t.stats.reviews, note: t.stats.reviewsNote, gold: false },
  ].filter((i) => i.value > 0);
  return (
    <section data-testid="platform-stats" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <p className={eyebrow}>{t.stats.eyebrow}</p>
        <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-6">
          {items.map((i) => (
            <li key={i.label} className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
              <p className={`bf-tabular font-headline text-5xl leading-none md:text-6xl ${i.gold ? "text-gold" : "text-chrome"}`}>
                {compactCount(i.value)}
              </p>
              <p className="mt-3 text-sm font-bold uppercase tracking-[0.12em] text-chrome">{i.label}</p>
              <p className="mt-1 text-xs text-chrome-dim">{i.note}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Paid producers from the landing feed's winners wall: usernames and cents.
export function ProducerEarnings({ winners }: { winners: LandingWin[] }) {
  const paid = winners.filter((w) => (w.payout_cents ?? 0) > 0).slice(0, 4);
  if (paid.length === 0) return null;
  return (
    <section data-testid="producer-earnings" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.earnings.eyebrow}</p>
          <h2 className={h2}>{t.earnings.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.earnings.sub}</p>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {paid.map((w) => (
            <li
              key={`${w.producer_username}:${w.contest_name}:${w.at}`}
              className="rounded-2xl border border-ground-edge bg-ground p-6 transition-colors hover:border-gold/40"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-lg font-bold text-chrome">@{w.producer_username}</div>
                  <div className="mt-1 truncate text-sm text-chrome-dim">{w.contest_name}</div>
                </div>
                <span className="rounded-full border border-money/30 bg-money/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-money">
                  {t.earnings.win}
                </span>
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-ground-edge pt-4">
                <p className="bf-tabular font-headline text-3xl leading-none text-gold">{formatMoney(w.payout_cents!)}</p>
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-gold/10 text-xl text-gold">
                  <Icon name="trophy" />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function Momentum({ activity }: { activity: Activity[] }) {
  if (activity.length === 0) return null;
  return (
    <section data-testid="momentum" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.momentum.eyebrow}</p>
          <h2 className={h2}>{t.momentum.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.momentum.sub}</p>
        </div>
        <ul className="mt-12 space-y-3">
          {activity.map((a, i) => (
            <li
              key={`${a.user}:${a.action}:${i}`}
              className="rounded-xl border border-ground-edge bg-ground-raised p-5 transition-colors hover:border-gold/40 md:p-6"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gold/10 text-xl text-gold">
                  <Icon name={a.type === "reaction" ? "video" : "trophy"} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <span className="font-bold text-chrome">{a.user}</span>
                      <span className="text-chrome-dim"> {a.action}</span>
                    </div>
                    {a.time && <span className="whitespace-nowrap text-xs text-chrome-dim">{a.time}</span>}
                  </div>
                  {a.prize && (
                    <div className="bf-tabular mt-2 inline-block rounded-full border border-gold/30 bg-gold/10 px-3 py-1 font-headline text-sm leading-none text-gold">
                      {a.prize}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-10 text-sm text-chrome-dim">{t.momentum.note}</p>
      </div>
    </section>
  );
}

export function Reactions({ reviews }: { reviews: PublicReview[] }) {
  if (reviews.length === 0) return null;
  return (
    <section id="examples" data-testid="reactions" className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.reactions.eyebrow}</p>
          <h2 className={h2}>{t.reactions.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.reactions.sub}</p>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {reviews.map((r, i) => {
            const media = r.videoURL ?? r.audioURL;
            return (
              <li key={`${r.reviewer}:${r.track}:${i}`} className="group overflow-hidden rounded-2xl border border-ground-edge bg-ground transition-colors hover:border-gold/50">
                <div className="relative flex aspect-video items-center justify-center bg-ground-raised">
                  {media ? (
                    <a
                      href={media}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={r.videoURL ? t.reactions.watch : t.reactions.listen}
                      className="bf-focusable flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-3xl text-gold transition-colors group-hover:bg-gold/25"
                    >
                      <Icon name="play" />
                    </a>
                  ) : (
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-3xl text-gold">
                      <Icon name="feedback" />
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-ground-edge bg-ground-raised font-headline uppercase text-gold">
                      {r.reviewer.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-chrome">{r.reviewer}</div>
                      {r.track && <div className="truncate text-xs text-chrome-dim">{t.reactions.reactedTo(r.track)}</div>}
                    </div>
                  </div>
                  {r.feedback && <p className="mt-4 line-clamp-4 text-sm leading-relaxed text-chrome-dim">&ldquo;{r.feedback}&rdquo;</p>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
